import { useState, useEffect, createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';

interface User {
  user_id: string;
  email: string;
  firstname: string;
  lastname: string;
  avatar: string | null;
  credit_balance: number;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      // Check if session is valid
      const authCheck = await api.get('/api/security/isAuthenicated');
      if (authCheck.data?.isAuthenicated) {
        // Fetch user details
        const meResponse = await api.get('/api/security/me');
        setUser(meResponse.data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string, password: string, remember = false) {
    const response = await api.post('/api/security/login', {
      email,
      password,
      rememberme: remember,
    });
    if (response.data?.identity) {
      await checkAuth();
    } else {
      throw new Error('Login failed — no identity returned');
    }
  }

  async function logout() {
    try {
      await api.post('/api/security/logout');
    } catch {
      // Ignore logout errors — clear local state anyway
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}